/* AV UNFOLD -- "Scan to Cut" chapter renderer (.av-edl--win[data-fx="unfold"]).
 * INTERACTIVE: a developable 3D point-cloud panel that the visitor scans with
 * the mouse. Scanned area stays lit (persistent coverage). Only once the whole
 * panel reaches ~100% does it announce "SURFACE CAPTURED" and unfold flat into a
 * marching cut-path -- AutoVectors "3D surface flattening" made literal. Idle /
 * touch falls back to an auto raster scan so it always completes. Accent from the
 * section's --acc CSS var. Raw WebGL1, IO/visibility/reduced-motion safe, dpr-
 * capped, mobile-lightened. Window-masked to .av-edl__divider + .av-edl__mock.
 * One instance per section; double-init guarded. */
(function () {
  if (window.__avUnfold) return; window.__avUnfold = 1;


  function hexToRgb(h) {
    h = (h || '').trim();
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h);
    if (m) return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
    var s = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(h);
    if (s) return [parseInt(s[1] + s[1], 16) / 255, parseInt(s[2] + s[2], 16) / 255, parseInt(s[3] + s[3], 16) / 255];
    return null;
  }
  function accentOf(host) {
    var sec = host.closest ? host.closest('.av-edl') : null;
    var v = sec ? getComputedStyle(sec).getPropertyValue('--acc') : '';
    return hexToRgb(v) || [0.91, 0.63, 0.23]; // default amber #e8a13a
  }
  function smoothstep(a, b, x) { x = Math.min(1, Math.max(0, (x - a) / (b - a))); return x * x * (3 - 2 * x); }

  function initOne(cv, host) {
    if (!cv || cv.__unf) return; cv.__unf = 1;
    var acc = accentOf(host);
    var dim = [acc[0] * 0.20, acc[1] * 0.20, acc[2] * 0.24];                  // un-scanned, faint
    var beamC = [0.13, 0.32, 1.0];                                          // saturated blue structured-light grid
    var targetC = [0.34, 0.55, 1.0];                                        // blue node accent (not white)
    var cut = [1.0, 0.48, 0.22];                                             // cut path orange
    var gl = cv.getContext('webgl', { alpha: false, antialias: true, premultipliedAlpha: false, powerPreference: 'low-power' })
          || cv.getContext('experimental-webgl');
    if (!gl) return;
    var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
    var coarse = (window.AV_PHONE === true) || (window.matchMedia && matchMedia('(hover:none),(pointer:coarse)').matches);   // AV_PHONE: physical phones (even in Chrome desktop-site mode) get the mobile treatment

    // HUD
    var hud = host.querySelector('.av-edl__scanhud');
    var hudTxt = hud && hud.querySelector('.av-edl__scantxt');
    var hudPct = hud && hud.querySelector('.av-edl__scanpct');
    var hudBar = hud && hud.querySelector('.av-edl__scanbar i');

    var winEls = host.querySelectorAll ? host.querySelectorAll('.av-edl__divider, .av-edl__mock') : [];
    var wins = new Float32Array(32);
    function computeWindows() {
      for (var k = 0; k < 32; k++) wins[k] = 9.0;
      var cr = cv.getBoundingClientRect(); if (cr.width < 1 || cr.height < 1) return;
      var n = Math.min(winEls.length, 8);
      for (var j = 0; j < n; j++) {
        var er = winEls[j].getBoundingClientRect();
        wins[j * 4] = (er.left - cr.left) / cr.width;
        wins[j * 4 + 1] = 1 - (er.bottom - cr.top) / cr.height;
        wins[j * 4 + 2] = (er.right - cr.left) / cr.width;
        wins[j * 4 + 3] = 1 - (er.top - cr.top) / cr.height;
      }
    }

    function sh(t, s) { var o = gl.createShader(t); gl.shaderSource(o, s); gl.compileShader(o); if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) console.warn(gl.getShaderInfoLog(o)); return o; }
    function prog(v, f) { var p = gl.createProgram(); gl.attachShader(p, sh(gl.VERTEX_SHADER, v)); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, f)); gl.linkProgram(p); return p; }

    /* ---------- panel: developable point grid, scan coverage + fold + cut-path ---------- */
    var PW = 4.2, PH = 2.35, BEND = 1.75, R = PW / BEND, PERIM = 2.0 * (PW + PH);
    var panVS = [
      'precision highp float;',
      'attribute vec2 aUV;attribute float aEdge;attribute float aArc;attribute float aSeed;attribute float aCov;attribute float aInside;',
      'uniform mat4 uMVP;uniform float uPx,uTime,uFold;',
      'uniform float uPW,uPH,uBend,uR;',
      'uniform vec2 uBrush;uniform float uBrushOn;uniform vec2 uCtr;',
      'uniform vec3 uAcc,uDim,uBeam,uCut,uTarget;',
      'varying vec3 vCol;varying float vGlow;',
      'void main(){float u=aUV.x,v=aUV.y;',
      ' vec3 fpos=vec3((u-0.5)*uPW,(v-0.5)*uPH,0.0);',           // flat hood pattern
      ' float cu=u-0.5,cv=v-0.5;',
      ' float dome=max(0.0,(1.0-cu*cu*3.3)*(1.0-cv*cv*1.9));',   // pronounced convex bonnet dome
      ' float zc=1.25*dome;',
      ' vec3 curv=vec3(fpos.x,fpos.y,zc);',                      // curved 3D hood (domed)
      ' vec3 pos=mix(curv,fpos,uFold);',
      ' pos.z+=sin(uTime*0.7+aSeed*6.28+u*5.0)*0.012*(1.0-uFold);',
      ' pos*=mix(0.92,1.0,uFold);',   // only a slight shrink while curved (uses the available height); full size once flat
      ' float sc=clamp(aCov,0.0,1.0);',
      // blue structured-light scanner: a projected diagonal crosshatch grid inside a round footprint
      ' vec2 q=aUV-uBrush;',
      ' float foot=smoothstep(0.19,0.085,length(q));',                  // circular scan footprint
      ' float r1=(q.x+q.y)*0.7071,r2=(q.x-q.y)*0.7071;',                // two 45deg diagonal axes
      ' float f1=abs(fract(r1*13.0)-0.5),f2=abs(fract(r2*13.0)-0.5);',
      ' float g1=smoothstep(0.07,0.0,f1),g2=smoothstep(0.07,0.0,f2);',  // thin parallel line sets
      ' float grid=max(g1,g2);',
      ' float beam=uBrushOn*foot*grid;',
      ' beam*=0.88+0.12*sin(uTime*7.0+(r1+r2)*40.0);',                  // subtle live shimmer
      ' float target=uBrushOn*foot*g1*g2;',                            // grid intersection nodes
      // marching cut-path along the boundary once flat
      ' float march=step(0.52,fract(aArc*5.0-uTime*0.5));',
      ' float cutp=aEdge*uFold*march;',
      ' float glow=sc*0.45+beam*0.5+target*0.55+cutp*0.9;vGlow=glow;',
      ' vec3 c=mix(uDim,uAcc,sc);',                  // faint -> amber as scanned
      ' c=mix(c,uBeam,clamp(beam,0.0,1.0));',        // blue structured-light grid
      ' c=mix(c,uTarget,clamp(target,0.0,1.0));',    // brighter blue nodes
      ' c=mix(c,uCut*1.2,cutp*0.75);',               // cut path hot orange
      ' float bri=0.6+0.4*fract(aSeed*91.7);vCol=c*bri;',
      ' vec4 cp=uMVP*vec4(pos,1.0);cp.xy+=uCtr*cp.w;gl_Position=cp;float w=max(cp.w,0.15);',   // uCtr: centre the hood on the DIVIDER WINDOW (mobile), not the whole sticky canvas
      ' float sz=(0.011+sc*0.006+beam*0.04+target*0.05+cutp*0.05)*uPx/w;',
      ' gl_PointSize=clamp(sz,1.0,34.0);',
      ' if(aInside<0.5){gl_PointSize=0.0;gl_Position=vec4(2.0,2.0,2.0,1.0);}}'
    ].join('');
    var panFS = [
      'precision mediump float;varying vec3 vCol;varying float vGlow;',
      'void main(){vec2 c=gl_PointCoord-0.5;float r=length(c);',
      ' float a=smoothstep(0.5,0.08,r);a*=a;',
      ' gl_FragColor=vec4(vCol*(0.72+1.7*vGlow),a);}'
    ].join('');
    var panP = prog(panVS, panFS);
    var panU = {
      mvp: gl.getUniformLocation(panP, 'uMVP'), px: gl.getUniformLocation(panP, 'uPx'),
      time: gl.getUniformLocation(panP, 'uTime'), fold: gl.getUniformLocation(panP, 'uFold'),
      pw: gl.getUniformLocation(panP, 'uPW'), ph: gl.getUniformLocation(panP, 'uPH'),
      bend: gl.getUniformLocation(panP, 'uBend'), r: gl.getUniformLocation(panP, 'uR'),
      brush: gl.getUniformLocation(panP, 'uBrush'), brushOn: gl.getUniformLocation(panP, 'uBrushOn'), ctr: gl.getUniformLocation(panP, 'uCtr'),
      acc: gl.getUniformLocation(panP, 'uAcc'), dim: gl.getUniformLocation(panP, 'uDim'),
      beam: gl.getUniformLocation(panP, 'uBeam'), cutc: gl.getUniformLocation(panP, 'uCut'),
      target: gl.getUniformLocation(panP, 'uTarget')
    };
    var aUV = gl.getAttribLocation(panP, 'aUV'), aEdge = gl.getAttribLocation(panP, 'aEdge'),
        aArc = gl.getAttribLocation(panP, 'aArc'), aSeed = gl.getAttribLocation(panP, 'aSeed'),
        aCov = gl.getAttribLocation(panP, 'aCov'), aIns = gl.getAttribLocation(panP, 'aInside');

    var GU = coarse ? 96 : 150, GV = coarse ? 52 : 82;
    var PN = GU * GV;
    var uvA = new Float32Array(PN * 2), edA = new Float32Array(PN), arA = new Float32Array(PN), seA = new Float32Array(PN);
    var inA = new Float32Array(PN);   // 1 = inside the car-hood silhouette, 0 = outside (hidden)
    var covA = new Float32Array(PN); // persistent scan coverage, 0..1
    // front-hood (bonnet) silhouette in (u,v): symmetric rounded trapezoid (wider at the rear cowl,
    // v=1) with rounded corners and a small grille notch at the front-centre (v=0). u left..right.
    function inHood(u, v) {
      if (u < 0.0 || u > 1.0 || v < 0.0 || v > 1.0) return false;
      var cu = u - 0.5;
      var topC = 0.86 + 0.13 * (cu / 0.5) * (cu / 0.5);   // curved top (rear) edge: dips in the middle, higher at the corners
      if (v > topC) return false;
      var hx = 0.37 + 0.11 * v;                          // trapezoid: narrower front (v=0) -> wider rear/top (v=1)
      if (v < 0.20) { var t = (0.20 - v) / 0.20; hx -= 0.13 * (1 - Math.sqrt(Math.max(0, 1 - t * t))); }   // round the front (bottom) corners
      if (Math.abs(cu) > hx) return false;
      var nx = (u - 0.5) / 0.055, ny = (v - 0.0) / 0.075;  // narrow grille notch at the front-centre
      if (nx * nx + ny * ny < 1.0) return false;
      return true;
    }
    var insideCount = 0, eu = 1.7 / (GU - 1), ev = 1.7 / (GV - 1), pi = 0;
    for (var jy = 0; jy < GV; jy++) {
      var v = jy / (GV - 1);
      for (var ix = 0; ix < GU; ix++) {
        var u = ix / (GU - 1), ins = inHood(u, v), edge = 0;
        if (ins) {
          insideCount++;
          if (!inHood(u + eu, v) || !inHood(u - eu, v) || !inHood(u, v + ev) || !inHood(u, v - ev) ||
              !inHood(u + eu, v + ev) || !inHood(u - eu, v - ev) || !inHood(u + eu, v - ev) || !inHood(u - eu, v + ev)) edge = 1;
        }
        uvA[pi * 2] = u; uvA[pi * 2 + 1] = v; inA[pi] = ins ? 1 : 0; edA[pi] = edge;
        arA[pi] = (Math.atan2(v - 0.5, u - 0.5) + Math.PI) / (2 * Math.PI); // angle around centroid -> cut-path marching
        seA[pi] = Math.random();
        pi++;
      }
    }
    if (insideCount < 1) insideCount = 1;
    function buf(a, dyn) { var b = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, b); gl.bufferData(gl.ARRAY_BUFFER, a, dyn ? gl.DYNAMIC_DRAW : gl.STATIC_DRAW); return b; }
    var uvB = buf(uvA), edB = buf(edA), arB = buf(arA), seB = buf(seA), inB = buf(inA), covB = buf(covA, true);

    /* ---------- ambient amber dust ---------- */
    var dVS = [
      'precision highp float;attribute vec3 aPos;attribute float aSeed;attribute float aS;',
      'uniform mat4 uMVP;uniform float uPx,uTime;varying float vT;',
      'void main(){vec3 wp=aPos;wp.y+=sin(uTime*0.3+aSeed*6.28+aPos.x*0.5)*0.07;wp.x+=cos(uTime*0.22+aSeed*5.0)*0.06;',
      ' vec4 cp=uMVP*vec4(wp,1.0);gl_Position=cp;float w=max(cp.w,0.15);',
      ' gl_PointSize=clamp(aS*uPx/w,1.0,22.0);vT=0.5+0.5*sin(uTime*1.2+aSeed*30.0);}'
    ].join('');
    var dFS = [
      'precision mediump float;varying float vT;uniform vec3 uAcc;',
      'void main(){vec2 c=gl_PointCoord-0.5;float r=length(c);float a=smoothstep(0.5,0.12,r);a*=a;',
      ' gl_FragColor=vec4(uAcc*(0.45+0.7*max(vT,0.0)),a);}'
    ].join('');
    var dP = prog(dVS, dFS);
    var dU = { mvp: gl.getUniformLocation(dP, 'uMVP'), px: gl.getUniformLocation(dP, 'uPx'), time: gl.getUniformLocation(dP, 'uTime'), acc: gl.getUniformLocation(dP, 'uAcc') };
    var dPos = gl.getAttribLocation(dP, 'aPos'), dSeed = gl.getAttribLocation(dP, 'aSeed'), dSz = gl.getAttribLocation(dP, 'aS');
    var DN = coarse ? 2600 : 5200;
    var dposA = new Float32Array(DN * 3), dseA = new Float32Array(DN), dszA = new Float32Array(DN);
    for (var di = 0; di < DN; di++) {
      dposA[di * 3] = (Math.random() * 2 - 1) * 5.2;
      dposA[di * 3 + 1] = (Math.random() * 2 - 1) * 2.3;
      var zr = Math.random(); dposA[di * 3 + 2] = (zr * zr * 2 - 1) * 4.6;
      dseA[di] = Math.random();
      dszA[di] = (Math.random() < 0.05 ? 0.045 : 0.012) * (0.7 + Math.random() * 0.7);
    }
    var dposB = buf(dposA), dseB = buf(dseA), dszB = buf(dszA);

    /* ---------- window mask ---------- */
    var mFS = [
      'precision highp float;uniform vec2 uRes;uniform vec4 uWin[8];uniform float uWinR;',
      'float winMask(vec2 uv){float m=0.0;for(int i=0;i<8;i++){vec4 w=uWin[i];vec2 cc=(w.xy+w.zw)*0.5;vec2 hh=(w.zw-w.xy)*0.5;vec2 d=abs(uv-cc)-hh+uWinR;float dist=length(max(d,0.0))+min(max(d.x,d.y),0.0)-uWinR;m=max(m,smoothstep(0.004,-0.004,dist));}return m;}',
      'void main(){vec2 uv=gl_FragCoord.xy/uRes;float m=winMask(uv);',
      ' gl_FragColor=vec4(0.0235,0.027,0.043,1.0-m);}'
    ].join('');
    var mP = prog('attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}', mFS);
    var quad = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, quad); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var mLoc = gl.getAttribLocation(mP, 'p');
    var mU = { res: gl.getUniformLocation(mP, 'uRes'), win: gl.getUniformLocation(mP, 'uWin[0]'), winR: gl.getUniformLocation(mP, 'uWinR') };

    /* ---------- matrices ---------- */
    function mPj(fy, asp, n, f) { var t = 1 / Math.tan(fy / 2); return [t / asp, 0, 0, 0, 0, t, 0, 0, 0, 0, (f + n) / (n - f), -1, 0, 0, (2 * f * n) / (n - f), 0]; }
    function mT(x, y, z) { return [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, x, y, z, 1]; }
    function mRX(a) { var c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1]; }
    function mRY(a) { var c = Math.cos(a), s = Math.sin(a); return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1]; }
    function mMul(a, b) { var o = new Float32Array(16); for (var c = 0; c < 4; c++) for (var r = 0; r < 4; r++) { var s = 0; for (var k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]; o[c * 4 + r] = s; } return o; }
    // project a model-space point through an mvp to a screen fraction (0=top/left .. 1=bottom/right)
    function projY(m, x, y, z) { var cy = m[1] * x + m[5] * y + m[9] * z + m[13], cw = m[3] * x + m[7] * y + m[11] * z + m[15]; return 0.5 - 0.5 * (cy / (cw || 1e-3)); }
    function projX(m, x, y, z) { var cx = m[0] * x + m[4] * y + m[8] * z + m[12], cw = m[3] * x + m[7] * y + m[11] * z + m[15]; return 0.5 + 0.5 * (cx / (cw || 1e-3)); }

    var W = 2, H = 2, dpr = 1, proj, uPx = 600, fov = 50 * Math.PI / 180;
    function setSize() {
      dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      var r = cv.getBoundingClientRect();
      W = Math.max(2, Math.round((r.width || 300) * dpr)); H = Math.max(2, Math.round((r.height || 300) * dpr));
      cv.width = W; cv.height = H; gl.viewport(0, 0, W, H); proj = mPj(fov, W / H, 0.1, 100); uPx = H * 0.5 / Math.tan(fov * 0.5);
    }
    setSize(); window.addEventListener('resize', setSize, { passive: true });

    /* ---------- interaction ---------- */
    var mx = 0.5, my = 0.5, brushU = 0.5, brushV = 0.5, lastMove = -9999, overCanvas = false;
    // screen-fraction projection of the hood's centre-line edges (top v=1, bottom v=0, left u=0, right u=1), refreshed each frame
    var panYv1 = 0.18, panYv0 = 0.42, panXu0 = 0.30, panXu1 = 0.70;
    var scroll = 0, yaw = 0.0, pitch = 0.1;
    function scrollProg() { var rr = host.getBoundingClientRect(); return Math.min(1, Math.max(0, (window.innerHeight * 0.5 - rr.top) / (rr.height + window.innerHeight))); }
    if (!coarse) {
      window.addEventListener('pointermove', function (e) {
        var r = cv.getBoundingClientRect(); if (e.clientY < r.top - 100 || e.clientY > r.bottom + 100) { overCanvas = false; return; }
        overCanvas = true;
        mx = (e.clientX - r.left) / r.width; my = (e.clientY - r.top) / r.height;
        // map cursor onto the panel's uv (panel ~ central 64% x / 56% y of the canvas)
        var du = (panXu1 - panXu0) || 1, dv = (panYv0 - panYv1) || 1;
        brushU = Math.min(1, Math.max(0, (mx - panXu0) / du));
        brushV = Math.min(1, Math.max(0, 1 - (my - panYv1) / dv));
        lastMove = performance.now();
      }, { passive: true });
    }
    var tchT = 0, tgSY = 0, tgX = 0, tgY = 0, tgCls = 0;   // MOBILE TOUCH PLAY + SCROLL/PLAY gesture classifier: vertical strokes or any real page scroll = SCROLL (brush untouched, gyro keeps running); horizontal-ish strokes on a still page = PLAY drags the scan brush
    function tgPlay(t) { if (tgCls === 2) return false; var sd = Math.abs((window.pageYOffset || 0) - tgSY); if (sd > (tgCls === 1 ? 30 : 4)) { tgCls = 2; tchT = 0; return false; } if (tgCls === 1) return true; var dx = Math.abs(t.clientX - tgX), dy = Math.abs(t.clientY - tgY); if (dx * dx + dy * dy < 144) return false; if (dy > dx * 1.2) { tgCls = 2; return false; } tgCls = 1; return true; }
    if (coarse) {
      window.addEventListener('touchstart', function (e) { var t = e.touches[0]; if (!t) return; tgSY = window.pageYOffset || 0; tgX = t.clientX; tgY = t.clientY; tgCls = 0; }, { passive: true });
      window.addEventListener('touchmove', function (e) { var t = e.touches[0]; if (!t) return; if (!tgPlay(t)) return; var r = cv.getBoundingClientRect(); if (t.clientY < r.top - 100 || t.clientY > r.bottom + 100) { overCanvas = false; return; } overCanvas = true; tchT = performance.now() + 900; mx = (t.clientX - r.left) / r.width; my = (t.clientY - r.top) / r.height; var du = (panXu1 - panXu0) || 1, dv = (panYv0 - panYv1) || 1; brushU = Math.min(1, Math.max(0, (mx - panXu0) / du)); brushV = Math.min(1, Math.max(0, 1 - (my - panYv1) / dv)); lastMove = performance.now(); }, { passive: true });
      window.addEventListener('touchend', function () { tgCls = 2; }, { passive: true });
      window.addEventListener('touchcancel', function () { tgCls = 2; }, { passive: true });
    }
    if (coarse && window.DeviceOrientationEvent) { var gyBX = null, gyBY = null;   // MOBILE GYRO LOOK: phone tilt gently sways the hood camera (yaw only; the scan brush keeps its auto-sweep)
      var gyOn = function (e) { if (performance.now() < tchT) return; var g = e.gamma, b = e.beta; if (g == null && b == null) return; g = g || 0; b = b || 0; if (gyBX === null) { gyBX = g; gyBY = b; } gyBX += (g - gyBX) * 0.006; gyBY += (b - gyBY) * 0.006; var dx = Math.max(-24, Math.min(24, g - gyBX)) / 24; mx = 0.5 + dx * 0.5; };
      var gyGo = function () { window.addEventListener('deviceorientation', gyOn, { passive: true }); };
      if (typeof DeviceOrientationEvent.requestPermission === 'function') { var gyReq = function () { window.removeEventListener('touchend', gyReq); (window.__avGyPr || (window.__avGyPr = DeviceOrientationEvent.requestPermission())).then(function (s) { if (s === 'granted') gyGo(); })['catch'](function () { }); }; window.addEventListener('touchend', gyReq, { passive: true }); }
      else gyGo();
    }
    var vis = true, wasVis = false;
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { vis = es[0].isIntersecting; }, { threshold: 0 }).observe(host);

    /* ---------- scan state machine ---------- */
    var DR = 0.18, DR2 = DR * DR;          // scan footprint radius (uv) for the projected grid
    var panBrush = new Float32Array([0.5, 0.5]);
    var phase = 'scan', coverage = 0, flatT = 0, autoT = 0, fold = 0, brushOn = 0, prevTm = 0;
    function resetScan() { covA.fill(0); coverage = 0; phase = 'scan'; fold = 0; autoT = 0; gl.bindBuffer(gl.ARRAY_BUFFER, covB); gl.bufferSubData(gl.ARRAY_BUFFER, 0, covA); if (hud) { hud.classList.remove('is-on'); hud.setAttribute('data-phase', 'scan'); if (hudTxt) hudTxt.textContent = 'SCANNING HOOD'; if (hudPct) hudPct.textContent = '0%'; if (hudBar) hudBar.style.width = '0%'; } }
    function paintDisc(bu, bv) {
      // structured-light footprint marks the disc it covers (max, soft falloff)
      for (var i = 0; i < PN; i++) {
        var du = uvA[i * 2] - bu, dv = uvA[i * 2 + 1] - bv, d2 = du * du + dv * dv;
        if (d2 < DR2) { var nv = 1 - d2 / DR2; nv = nv * nv * (3 - 2 * nv); if (nv > covA[i]) covA[i] = nv; }
      }
    }

    var t0 = performance.now(), raf = 0, fc = 0;
    function render(tm) {
      var dt = Math.min(0.05, tm - prevTm); prevTm = tm; if (dt < 0) dt = 0.016;
      scroll += (scrollProg() - scroll) * 0.08;

      // ----- scanning logic -----
      var userActive = overCanvas && (performance.now() - lastMove < 1900);
      brushOn = 0;
      if (phase === 'scan') {
        if (userActive) {
          paintDisc(brushU, brushV); brushOn = 1;
          panBrush[0] = brushU; panBrush[1] = brushV;
        } else {
          // auto: scan head sweeps horizontally while drifting up & down -> covers the whole hood incl. corners
          autoT += dt;
          var tri = Math.abs(((autoT * 0.12) % 2.0) - 1.0);
          var bv = 0.08 + 0.84 * tri;
          var bu = 0.5 + 0.5 * Math.sin(autoT * 3.6);
          paintDisc(bu, bv); brushOn = 1;
          panBrush[0] = bu; panBrush[1] = bv;
        }
        gl.bindBuffer(gl.ARRAY_BUFFER, covB); gl.bufferSubData(gl.ARRAY_BUFFER, 0, covA);
        if ((fc & 3) === 0) { var cnt = 0; for (var k = 0; k < PN; k++) if (inA[k] > 0.5 && covA[k] > 0.45) cnt++; coverage = cnt / insideCount; }
        if (coverage >= 0.97 || autoT > 24) {
          phase = 'flatten'; flatT = tm;
          if (hud) { hud.setAttribute('data-phase', 'done'); if (hudTxt) hudTxt.textContent = 'HOOD CAPTURED — FLATTENING'; if (hudPct) hudPct.textContent = '100%'; if (hudBar) hudBar.style.width = '100%'; }
        } else if (hud) {
          hud.classList.add('is-on');
          if (hudPct) hudPct.textContent = Math.round(coverage * 100) + '%';
          if (hudBar) hudBar.style.width = (coverage * 100).toFixed(0) + '%';
        }
      } else if (phase === 'flatten') {
        fold = smoothstep(0, 1, (tm - flatT) / 2.2);
        if (tm - flatT > 2.2) { fold = 1; phase = 'flat'; flatT = tm; }
      } else if (phase === 'flat') {
        fold = 1;
        if (tm - flatT > 8.0) { resetScan(); }
      }

      // ----- camera: gentle fixed pose while scanning, face-on on flatten -----
      var tgtPitch = 0.05 - 0.40 * (1.0 - fold), tgtYaw = (0.38 + 0.08 * (mx - 0.5)) * (1.0 - fold);
      yaw += (tgtYaw - yaw) * 0.06; pitch += (tgtPitch - pitch) * 0.06;
      var camDist = (4.4 - (coarse ? 2.2 : 0.8) * scroll - 0.3 * fold) * Math.max(1.0, 1.28 * H / W);   // portrait/narrow phones: the vertical-FOV projection clips a wide object horizontally -> push the camera back proportionally so the whole hood (PW=4.2) fits the screen WIDTH (edges visible); desktop (W/H>=1.28) unchanged. MOBILE: 2.2 scroll-zoom (vs 0.8) -> a clear hero-globe-style "flying closer" as you scroll the chapter
      computeWindows();
      var yLift = 0.42 + 0.20 * fold; // lift the 3D hood up (so its bottom shows + fills the top); a bit more once flat
      var mvp = mMul(proj, mMul(mT(0, yLift, -camDist), mMul(mRY(yaw), mRX(pitch))));
      // project the hood's centre-line edges through the REAL mvp so the cursor maps onto where the hood actually appears
      // (panel point: pos = (fpos.x, fpos.y, zc*(1-fold)) * s; centre bulges toward camera with zc up to 1.25)
      var ps = 0.92 + 0.08 * fold, k1f = (1 - fold) * ps;
      var pyh = 1.175 * ps, pzTB = 0.656 * k1f, pxh = 2.1 * ps, pzLR = 0.219 * k1f;
      panYv1 = projY(mvp, 0, pyh, pzTB); panYv0 = projY(mvp, 0, -pyh, pzTB);
      panXu1 = projX(mvp, pxh, 0, pzLR); panXu0 = projX(mvp, -pxh, 0, pzLR);
      var ctrX = 0, ctrY = 0;   // MOBILE: pin the HOOD'S projected centre onto the VIEWPORT centre -- a CONSTANT screen-fixed pose (like the hero globe), no divider-chasing easing. (-hn auto-compensates yLift/dome -> no "hood high, empty bottom".)
      if (coarse) {
        var hnx = (panXu0 + panXu1) - 1.0, hny = 1.0 - (panYv0 + panYv1);   // hood centre, NDC, from its RAW projected edge fractions (projX=0.5+ndc/2, projY=0.5-ndc/2)
        ctrX = Math.max(-1.4, Math.min(1.4, -hnx)); ctrY = -hny;
      }
      var crv = cv.getBoundingClientRect(); if (crv.height > 1) ctrY += 2 * crv.top / crv.height;   // VIEWPORT-PIN (desktop + mobile): the sticky canvas is NOT pinned while the chapter enters/leaves (cr.top>0/<0), so anything anchored to the CANVAS centre sat too low behind the black layer during entry ("matosi tik pats virsus"); this term re-anchors the scene to the SCREEN centre the whole way -- 0 while pinned (cr.top=0), so the tuned pinned-phase look is untouched. cr.height (stable 100vh) as the vh proxy dodges the mobile address-bar innerHeight jitter.
      ctrY = Math.max(-2.5, Math.min(2.5, ctrY));
      panYv1 -= 0.5 * ctrY; panYv0 -= 0.5 * ctrY; panXu0 += 0.5 * ctrX; panXu1 += 0.5 * ctrX;   // keep the cursor/touch->brush mapping aligned with the SHIFTED hood (projY=0.5-0.5*ndcY, so ndcY+=ctrY => f-=ctrY/2; projX analogously)

      gl.clearColor(0.0235, 0.027, 0.043, 1.0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

      // dust
      gl.useProgram(dP);
      gl.bindBuffer(gl.ARRAY_BUFFER, dposB); gl.enableVertexAttribArray(dPos); gl.vertexAttribPointer(dPos, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, dseB); gl.enableVertexAttribArray(dSeed); gl.vertexAttribPointer(dSeed, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, dszB); gl.enableVertexAttribArray(dSz); gl.vertexAttribPointer(dSz, 1, gl.FLOAT, false, 0, 0);
      gl.uniformMatrix4fv(dU.mvp, false, mvp); gl.uniform1f(dU.px, uPx); gl.uniform1f(dU.time, tm); gl.uniform3f(dU.acc, acc[0] * 0.7, acc[1] * 0.7, acc[2] * 0.7);
      gl.drawArrays(gl.POINTS, 0, DN);

      // panel
      gl.useProgram(panP);
      gl.bindBuffer(gl.ARRAY_BUFFER, uvB); gl.enableVertexAttribArray(aUV); gl.vertexAttribPointer(aUV, 2, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, edB); gl.enableVertexAttribArray(aEdge); gl.vertexAttribPointer(aEdge, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, arB); gl.enableVertexAttribArray(aArc); gl.vertexAttribPointer(aArc, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, seB); gl.enableVertexAttribArray(aSeed); gl.vertexAttribPointer(aSeed, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, covB); gl.enableVertexAttribArray(aCov); gl.vertexAttribPointer(aCov, 1, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ARRAY_BUFFER, inB); gl.enableVertexAttribArray(aIns); gl.vertexAttribPointer(aIns, 1, gl.FLOAT, false, 0, 0);
      gl.uniformMatrix4fv(panU.mvp, false, mvp); gl.uniform1f(panU.px, uPx); gl.uniform1f(panU.time, tm); gl.uniform1f(panU.fold, fold);
      gl.uniform1f(panU.pw, PW); gl.uniform1f(panU.ph, PH); gl.uniform1f(panU.bend, BEND); gl.uniform1f(panU.r, R);
      gl.uniform2f(panU.brush, panBrush[0], panBrush[1]); gl.uniform1f(panU.brushOn, brushOn * (1 - fold)); gl.uniform2f(panU.ctr, ctrX, ctrY);
      gl.uniform3f(panU.acc, acc[0], acc[1], acc[2]); gl.uniform3f(panU.dim, dim[0], dim[1], dim[2]);
      gl.uniform3f(panU.beam, beamC[0], beamC[1], beamC[2]); gl.uniform3f(panU.cutc, cut[0], cut[1], cut[2]);
      gl.uniform3f(panU.target, targetC[0], targetC[1], targetC[2]);
      gl.drawArrays(gl.POINTS, 0, PN);

      // window mask
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.useProgram(mP);
      gl.bindBuffer(gl.ARRAY_BUFFER, quad); gl.enableVertexAttribArray(mLoc); gl.vertexAttribPointer(mLoc, 2, gl.FLOAT, false, 0, 0);
      gl.uniform2f(mU.res, W, H); gl.uniform4fv(mU.win, wins); gl.uniform1f(mU.winR, 0.014);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      fc++;
    }

    function frame(now) {
      var r = cv.getBoundingClientRect();
      var nW = Math.max(2, Math.round((r.width || 300) * dpr)), nH = Math.max(2, Math.round((r.height || 300) * dpr));
      if (Math.abs(nW - W) > 2 || Math.abs(nH - H) > 2) { W = nW; H = nH; cv.width = W; cv.height = H; gl.viewport(0, 0, W, H); proj = mPj(fov, W / H, 0.1, 100); uPx = H * 0.5 / Math.tan(fov * 0.5); }
      if (vis) { if (!wasVis) { resetScan(); prevTm = (now - t0) / 1000; } wasVis = true; render((now - t0) / 1000); }
      else { wasVis = false; }
      raf = requestAnimationFrame(frame);
    }
    if (reduce) { covA.fill(1); gl.bindBuffer(gl.ARRAY_BUFFER, covB); gl.bufferSubData(gl.ARRAY_BUFFER, 0, covA); fold = 1; phase = 'flat'; render(6.0); }
    else { raf = requestAnimationFrame(frame); }
    document.addEventListener('visibilitychange', function () { if (document.hidden) { cancelAnimationFrame(raf); raf = 0; } else if (!raf && !reduce) { t0 = performance.now() - prevTm * 1000; raf = requestAnimationFrame(frame); } });
  }

  function collect(root) {
    var out = [];
    var wn = root.querySelectorAll ? root.querySelectorAll('.av-edl--win[data-fx="unfold"]') : [];
    for (var i = 0; i < wn.length; i++) { var c = wn[i].querySelector('.av-edl__bg .av-edl__canvas'); if (c) out.push([c, wn[i]]); }
    return out;
  }
  function initAll() { var l = collect(document); for (var i = 0; i < l.length; i++) initOne(l[i][0], l[i][1]); }
  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', function (e) { var l = collect(e.target); for (var i = 0; i < l.length; i++) initOne(l[i][0], l[i][1]); });
})();
